import {cast, flow, getEnv, Instance, SnapshotOut, types} from "mobx-state-tree";
import intl from "react-intl-universal";

import {PublicationState, Quiz} from "../../@types/Quiz";
import {AppSyncWebSocketClient} from "../gqlModel/AppSyncWebSocketClient";
import {Quiz as QuizServer} from "./quiz-api";

import {QuizQuestionModel, QuizQuestionModelType} from "./QuizQuestionModel";
import {getGqlModelBase} from "../gqlModel/GqlModelBase";
import type {GQLQueryProvider} from "../gqlModel/GQLQueryProvider";
import type {QuizGqlProvider} from "./QuizGqlProvider";
import type {QuizQuestionGQLProvider} from "./QuizQuestionGQLProvider";
import {logErrorToSentry} from "../../utils/errors/logErrorToSentry";
import {listQuizQuestions} from "./requests/listQuizQuestions";
import {quizServerToQuizMSTSnapshotIn} from "../convertors/quizServerToQuizMSTSnapshotIn";

import {onUpdateQuiz} from "../../graphql/quiz/subscriptions";
import {IDataClientService} from "../../platform/services/data/DataClientService";
import {IAuthService} from "../../platform/services/auth/AuthService";

export interface QuizModelEnv {
    getDataClientService: () => IDataClientService;
    authService: IAuthService;
    queryProvider?: QuizGqlProvider;
    getQueryProvider?: (node: any) => GQLQueryProvider;
}

export type QuizModelType = Instance<typeof QuizModel>;


/* A graphql query fragment builders for QuizModel */
/**
 * QuizModel
 */
export const QuizModel = getGqlModelBase<Quiz>()
    .named("QuizModel")
    .props({
        id: types.optional(types.identifier, () => crypto.randomUUID()),
        ownerIdAndWsId: types.string,
        creator: types.string,
        version: types.optional((types.integer), 0),
        name: types.optional(types.string, ''),
        description: types.optional(types.string, ''),
        publication_state: types.optional(types.string, 'draft'),
        relatedPages: types.optional(types.maybeNull(types.string), ''),
        latestEditor: types.optional(types.string, ''),

        isQuestionsLoading: types.optional(types.boolean, false),
        isInitialized: types.optional(types.boolean, false),
        isInitializing: types.optional(types.boolean, false),

        createdAt: types.optional(types.string, ''),
        updatedAt: types.optional(types.string, ''),

        requiredPassPercentage: (types.maybe(types.integer)),
        questionsCount: types.optional(types.integer, 0),
        questionsOrder: types.array(types.string),

        questions: types.map(QuizQuestionModel),
        deletedQuestionIds: types.array(types.string),
    })
    .views(self => ({
        get wsId(): string {
            return self.lastSavedObject.ownerIdAndWsId.split('#')[1];
        },
        get nameWithPlaceholder(): string {
            return self.name
                ? self.name
                : intl.get('quiz.untitled').d('Untitled Test')
        },
        get safeQuestionsOrder(): string[] {
            return self.questionsOrder.filter(questionId => !!self.questions.get(questionId));
        },
        get hasChangesRecursive(): boolean {
            if (self.hasChanges || self.deletedQuestionIds.length) {
                return true;
            } else {
                for (const quizQuestion of self.questions.values()) {
                    if (quizQuestion.hasChanges) {
                        return true;
                    }
                }
            }
            return false;
        },
        get hasError() {
            if (!self.name) {
                return true;
            }
            for (const question of self.questions.values()) {
                if (question.hasError) {
                    return true;
                }
            }
            return false;
        }
    }))
    .volatile(self => ({
        wsClient: null as AppSyncWebSocketClient | null
    }))
    .actions(self => {
        const beforeDestroy = () => {
            if (self.wsClient) {
                self.wsClient.cancelSubscription(`onUpdateQuiz_detail`);
            }
        };

        return {
            proceedSaveResults(obj: QuizServer) {
                // @ts-ignore
                self.lastSavedObject = quizServerToQuizMSTSnapshotIn(obj).lastSavedObject!;
            },
            beforeDestroy,
        };
    })
    .actions(self => {
        const loadQuestions = flow(function* () {
            try {
                self.isQuestionsLoading = true;
                const {getDataClientService, authService} = getEnv<QuizModelEnv>(self);
                const questionsResponse = yield listQuizQuestions(
                    getDataClientService(),
                    authService,
                    self.wsId,
                    self.id
                );
                for (const quizQuestionBackend of questionsResponse.items) {
                    const parsedOptions = typeof quizQuestionBackend.options === 'string' 
                        ? JSON.parse(quizQuestionBackend.options)
                        : quizQuestionBackend.options;
                    
                    self.questions.set(quizQuestionBackend.id, {
                        ...quizQuestionBackend,
                        options: parsedOptions,
                    });
                    if (self.questionsOrder.indexOf(quizQuestionBackend.id) === -1) {
                        self.questionsOrder.push(quizQuestionBackend.id);
                    }
                }
                self.isQuestionsLoading = false;
            } catch (error) {
                self.isQuestionsLoading = false;
                self.error = error;
                logErrorToSentry(error as Error);
            }
        });

        return {
            loadQuestions,
        }
    })
    .actions(self => {
        const init = flow(function* () {
            try {
                self.isInitializing = true;
                if (!self.isNew) {
                    yield self.loadQuestions();
                }

                if (!self.wsClient) {
                    const {getDataClientService} = getEnv<QuizModelEnv>(self);

                    const dataClientService = getDataClientService();
                    self.wsClient = yield dataClientService.getWsClientByKey('pgServer');
                }
                
                const subscriptionResult = self.wsClient!.registerSubscription(
                    `onUpdateQuiz_detail`, 
                    onUpdateQuiz, 
                    (updatedItem?: QuizServer) => {
                        if (!updatedItem || updatedItem.id !== self.id) {
                            return;
                        }
                        self.setLastSavedObject(quizServerToQuizMSTSnapshotIn(updatedItem).lastSavedObject!);
                    }
                );
                
                // Only yield if registerSubscription returns a promise
                if (subscriptionResult && typeof subscriptionResult.then === 'function') {
                    yield subscriptionResult;
                }
                
                self.isInitializing = false;
                self.isInitialized = true;
            } catch (error) {
                self.isInitializing = false;
                self.error = error;
                logErrorToSentry(error as Error);
            }
        });

        return {
            init,
        }
    })
    .actions(self => ({
        setRelatedPages(relatedPages: string | null) {
            self.relatedPages = relatedPages;
            self.hasChanges = true;
        },
        setDescription(description: string) {
            self.description = description;
            self.hasChanges = true;
        },
        setRequiredPassPercentage(requiredPassPercentage: number) {
            self.requiredPassPercentage = requiredPassPercentage;
            self.hasChanges = true;
        },
        setName(name: string) {
            self.name = name;
            self.hasChanges = true;
        },
        setPublicationState(publication_state: PublicationState) {
            self.publication_state = publication_state;
            self.hasChanges = true;
        },
        addQuestion(quizQuestion: QuizQuestionModelType | SnapshotOut<QuizQuestionModelType>, positionIndex?: number) {
            self.questions.set(quizQuestion.id, quizQuestion);
            self.questionsCount += 1;
            const newOrder: string[] = [];
            let haveInserted = false;
            for (const [index, value] of self.questionsOrder.entries()) {
                if (index === positionIndex) {
                    newOrder.push(quizQuestion.id);
                    haveInserted = true;
                }
                newOrder.push(value);
            }
            if (!haveInserted) {
                newOrder.push(quizQuestion.id);
            }
            self.questionsOrder = cast(newOrder);
            self.hasChanges = true;

            if (quizQuestion.id && self.deletedQuestionIds.includes(quizQuestion.id)) {
                self.deletedQuestionIds.remove(quizQuestion.id);
            }
        },
        setQuestionsOrder(newOrder: string[]) {
            self.questionsOrder = cast(newOrder);
            self.hasChanges = true;
        },
        reorder(startIndex: number, endIndex: number) {
            const result = Array.from(self.questionsOrder);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);

            self.questionsOrder = cast(result);
            self.hasChanges = true;
        },
        removeQuestion(quizQuestion: QuizQuestionModelType) {
            if (!quizQuestion.isNew) {
                self.deletedQuestionIds.push(quizQuestion.id);
            }

            self.questionsCount -= 1;
            self.questionsOrder = cast(self.questionsOrder.filter(questionId => questionId !== quizQuestion.id));
            self.hasChanges = true;
            self.questions.delete(quizQuestion.id);
        },
    }))
